import * as bigInt from 'big-integer';
import { BehaviorSubject, Subject } from 'rxjs';
import { CrossWindowEvents, WebSocketEventTypes } from './crossWindowProxy';

export const bigIntZero = bigInt.zero;
export type BigInteger = bigInt.BigInteger;

export enum OsTypes {
    Unknown = 0,

    // WinCe
    WinCeMin = 10000,
    WinCe = 10000,
    WinCeNet = 10001,
    WinCePocketPc = 10002,
    WinCeSmartphone = 10003,
    WinCeWindowsMobile = 10004,
    WinCeMax = 20000,

    // Win Desktop
    WindowsMin = 30000,
    Windows = 30000,
    Windows95 = 30001,
    Windows98 = 30002,
    WindowsMe = 30003,
    WindowsNt351 = 30004,
    WindowsNt4 = 30005,
    Windows2000 = 30006,
    WindowsXp = 30007,
    WindowsServer2003 = 30008,
    WindowsVista = 30009,
    Windows7 = 30010,
    WindowsServer2008 = 30011,
    Windows8 = 30012,
    WindowsServer2012 = 30013,
    Windows81 = 30014,
    Windows10 = 30015,
    WindowsServer2016 = 30016,
    WindowsMax = 39999,

    // Universal Windows
    UniversalWindows = 40000,

    // iOS
    IosMin = 70000,
    Ios = 70000,
    IosMax = 80000,

    // Android
    AndroidMin = 90000,
    Android = 90000,
    AndroidMax = 99999,

    OsMax = 100000

}

export interface ISystemInfo {
    main: IMainSystemInfo;
    platformSpecific: IWindowsSystemInfo | IWindowsCeSystemInfo | IUniversalWindowsSystemInfo | IIOSSystemInfo | IAndroidSystemInfo;
}

export interface IMainSystemInfo {
    sysInfoSize: number;

    // System Info
    osType: number;
    cpuType: number;
    cpuSize: number;
    cpuCount: number;
    cpuSpeed: number;
    osVersion: number;
    osBuild: number;
    agentVersion: number;

    // Memory Info
    totalMemory: BigInteger;
    freeMemory: BigInteger;
    totalVirtualMemory: BigInteger;
    freeVirtualMemory: BigInteger;

    // Storage Info
    totalStorage: BigInteger;
    freeStorage: BigInteger;

    // Display Info
    scrWidth: number;
    scrHeight: number;
    bitsPerPixel: number;

    // Network
    connectionType: number;
}

export interface IWindowsSystemInfo {
    memoryLoad: number; // DWORD
    pageSize: number; // DWORD
    minAppAddress: BigInteger; // QWORD
    maxAppAddress: BigInteger; // QWORD
    monitors: number; // DWORD
    versionInfo: string; // WCHAR[80]
    processorName: string; // WCHAR[80]

    // Battery
    batteryLifetime: number; // DWORD
    batteryFullLifeTime: number; // DWORD
    batteryFlag: number; // BYTE
    batteryLifePercent: number; // BYTE
    batteryReserved: number[]; // BYTE[2]

    acLineStatus: number; // BYTE
    reserved: number[]; // BYTE[3]
    monitorsInfo?: IMonitorInfo[];
}

export interface IWindowsCeSystemInfo extends IWindowsSystemInfo {
    backupBatteryLifetime: number; // DWORD
    backupBatteryFullLifeTime: number; // DWORD
    backupBatteryFlag: number; // BYTE
    backupBatteryLifePercent: number; // BYTE
    backupBatteryReserved: number[]; // BYTE[2]
}

export interface IUniversalWindowsSystemInfo {
    // Battery
    batteryFlag: number; // BYTE
    batteryLifePercent: number; // BYTE
}

export interface IAndroidSystemInfo {
    batteryStatus: number; // DWORD
    batteryLevel: number; // DWORD
    batteryVoltage: number; // DWORD
    batteryTemperature: number; // DWORD

    // monitor info
    monitorNumber: number; // DWORD
    monitorList: IMonitorInfo[];
}

export interface IMonitorInfo {
    name: string;
    id: number; // DWORD
    width: number; // DWORD
    height: number; // DWORD,
    rotation$: BehaviorSubject<number>;
    rotation: number;
    top: number;
    left: number;
}

export interface IIOSSystemInfo {
    batteryStatus: number; // DWORD
    batteryLevel: number; // DWORD
    deviceModel: string; // WCHAR[20]
    deviceName: string; // WCHAR[40]
}

export interface IOSInfo {
    IsWindows(osType: number): boolean;
    IsWindowsCe(osType: number): boolean;
    IsAndroid(osType: number): boolean;
    IsUniversalWindows(osType: number): boolean;
    IsIOS(osType: number): boolean;
    IsLinux(osType: number): boolean;
    IsTizen(osType: number): boolean;
}

export interface IInitAuthSession {
    token: string;
    sessionId: string;
}

export interface IInitRequestPacket extends IInitAuthSession {
    deviceId: string;
    userName: string;
    keys: string;
    host: string;
    versionHigh: number;
    versionLow: number;
    sotiAssistDeviceId: string;
    forceOverride: boolean;
    disableUserDisplay?: boolean;
    disableUserInteraction?: boolean;
}

export interface ILoginResponsePacket {
    returnCode: number;
    hasScreenInfo: boolean;
    hasModelInfo: boolean;
    width?: number;
    height?: number;
    rotation?: number;
    model: string;
    permissions?: BigInteger;
    osType: number;
}

export interface IInitResponsePacket extends ILoginResponsePacket {
    versionHigh: number;
    versionLow: number;
    payloadFlags: number;
    osType: number;
}

export interface IFrameData {
    binImageData: Uint8Array;
    x: number;
    y: number;
}

export interface IScreenPacket {
    frameData?: IFrameData[];
    numImage?: number;
    scale: number;
    rotation: number;
    imageDataFlags: number;
    ackRequired: boolean;
    fullscreen: boolean;
    isJpegCodec?: boolean;
    mp4?: Uint8Array;
}

export interface IServerStatus {
    status: number;
    message: string;
}

export class CrossWindowMessage {
    public type: CrossWindowEvents | WebSocketEventTypes;
    public data: any;
    constructor(type: CrossWindowEvents | WebSocketEventTypes, data: any ) {
        this.type = type;
        this.data = data;
    }
};

export declare type ConnectHandler = () => void;
export declare type CloseHandler = (evt: CloseEvent) => void;
export declare type ErrorHandler = (msg: string) => void;

export interface IMessageHandler {
    onMessage(event: Event);
    onConnect(event: Event);
    onClose(event: Event);
    onError(event: Event);

    setOnConnect(callback: ConnectHandler);
    setOnClose(callback: CloseHandler);
}

export enum ScreenMessageFlags {
    DeviceProjection = 0x08,
    JpegCodec = 0x40,
    RotationNonWindows = 0x80,
    IsReversedImage = 0x01000000,
    CompressedJFIF = 0x02000000,
    AckRequired = 0x08000000
}

export enum MessageFlags {
    COMPRESSED = 0x01,
    ENCRYPTED = 0x02,
    IS_REQUEST = 0x04,
    IS_RESPONSE = 0x08,
    HAS_ERRORCODE = 0x10,
    HAS_REQID = 0x20,
    IS_ZIPCOMPRESSED = 0x40,
    MOREDATA = 0x80
}

export enum MessageType {
    PC_STOP = 0,
    PC_INIT = 1,
    PC_LOGIN = 2,
    PC_PING = 4,
    PC_PONG = 5,
    PC_TH_ACK = 9,
    PC_RCPAUSE = 10,
    // PC_RCRESUME = 11,
    PC_ADMIN = 12,
    PC_GETSYSINFO = 20,
    PC_QUERY_DEVICE = 22,
    PC_RESET = 23,				// Perform a soft reset of the remote device, reboot device
    PC_HARDRESET = 24,			// Perform a hard reset of the remote device, reboot, return factory settings
    KEYBOARD_SEND = 40,
    KEYBOARD_POST = 43,
    KEYBOARD_CTRLED_ALTED_SEND = 45,
    PC_APPBUTTON = 46,
    MOUSE = 47,
    PC_FULLSCREEN = 60,
    PC_DELTASCREEN = 61,
    PC_RESTART = 62,
    PC_GETTSKLIST = 70,
    PC_KILLTSK = 71,
    // PC_GETTSKSTAT = 73,
    PC_GETTHRANDMODLISTS = 74,
    PC_GETTHREADTIMES = 75,
    PC_DOS_DIR = 92,
    PC_DOS_SEARCH = 93,
    PC_DOS_DEL = 95,
    PC_DOS_MKDIR = 98,
    PC_DOS_RMDIR = 99,
    PC_DOS_RENAME = 100,
    PC_DOS_SEND_FILE = 101,
    PC_DOS_GET_FILE = 102,
    PC_DOS_SEND_FILE_DATA = 103,
    PC_DOS_GET_FILE_DATA = 104,
    PC_DOS_EMPTY_FOLDER = 107,
    PC_CANCEL_ASYNC = 112,
    PC_DOS_COMPRESSION = 113,
    PC_DEVICE2DESK_NOTIFICATION = 161,
    PC_ANDROID_ACTIVITY_MANAGEMENT = 200,
    // PC_CLIENT_VIDEO_RECORDING = 201,
    PC_AGENT_RECORD = 210,
    PC_ANNOTATION = 211,
    PC_AGENT_DUMP_LOG = 212
}

export enum ClientVideoRecordingEventType {
    Start = 0,
    Stop = 1,
    Pause = 2,
    Resume = 3,
    UploadComplete = 4,
}

export enum ScaleSize {
    SCALE_MIN = 5,
    SCALE_MAX = 300,
    SCALE_STEP_DOWN = -10,
    SCALE_STEP_UP = 10,
    SCALE_RESIZE = 1000000,
}

export enum ColorQuality {
    ColorQuality32Bpp = 0,
    ColorQuality16Bpp = 8,
    ColorQuality8Bpp = 4,
    ColorQuality4Bpp = 2,
    ColorQuality2Bpp = 1
}

const SCALE_AUTO = 501;
const SCALE_CUSTOM = 502;
const SCALE_UNKNOWN = -1;

// DeviceScale used in a 2024 RC Refactoring
export enum DeviceScale {
    ScaleAuto = SCALE_AUTO,
    Scale100 = 100,
    Scale80 = 80,
    Scale50 = 50,
    Scale25 = 25,
    ScaleCustom = SCALE_CUSTOM,
    ScaleUnknown = SCALE_UNKNOWN,
}

export enum CommMessageType {
    REMOTE_CONTROL_MESSAGE = 100
}

export enum SignatureByte {
    SignatureCommMessage = 0xD5,
    SignatureRemoteControlMessage = 0xD6,
    SignatureSotiAssistMessage = 0xD7,
    SignatureStreamingRCMessage = 0xD8 // used to be this.mcrc.consts.SignatureByte.SignatureCommMessage (0xD5), changed to 0xD8 to avoid conflicts with Comm messages.
}

export enum ReturnCode {
    PCR_OK = 0,
    PCR_TIMEOUT = 2,
    PCR_LOGIN_REQUIRED = 4,
    PCR_LOGIN_REJECTED = 6,
    PCR_ALREADY_CONNECTED = 10,
    PCR_USER_LOGGED_OUT = 13
}

export enum SpecialKeyCode {
    BACKSPACE = 8,
    TAB = 9,
    CR = 13,
    END = 35,
    HOME = 36,
    ARROWLEFT = 37,
    ARROWUP = 38,
    ARROWRIGHT = 39,
    ARROWDOWN = 40,
    DELETE = 46
}

export enum SotiAssistMessageType {
    SAC_VIDEO_RECORDING_START = 1,
    SAC_VIDEO_RECORDING_STOP = 2,
    SAC_VIDEO_RECORDING_PAUSE = 3,
    SAC_SET_ATTACHMENT_TICKET_ID = 4,
    SAC_VIDEO_RECORDING_SETUP = 5,
    SAC_VIDEO_FILE_NAME = 6,
    SAC_SERVER_STATUS = 7,
    SAC_GET_VIDEO_DOWNLOAD_LINK = 8, // deprecated
    SAC_CLIENT_SIDE_ROTATION = 9,
    SAC_DEVICE_CAPTURE_ACTIVITY_CREATED = 10,
    SAC_DEVICE_LOGS_REQUEST_CANCELED = 11,
    SAC_VIDEO_RECORDING_CANCEL = 12,
    SAC_DEVICE_LOGS_DOWNLOAD_READY = 13,
    SAC_VIDEO_RECORDING_DOWNLOAD_READY = 14,
    SAC_DEVICE_LOGS_REQUEST_COMPLETED = 15
}

export enum AgentDumpLogMessageType {
    PC_AGENT_DUMP_LOG_REQUEST = 1,
    MC_DEBUG_REPORT_QUERY = 0x10,
    MC_DEBUG_REPORT_BY_LIST = 0x02
}

export enum AgentRecordingMessageType {
    ARM_START_RECORDING = 1,
    ARM_PAUSE_RECORDING = 2,
    ARM_RESUME_RECORDING = 3,
    ARM_STOP_RECORDING = 4,
    ARM_GET_RECORDING = 5
}

export enum AgentRecordingStopReason {
    Unspecified = -1,
    RequestedByUser = 0,
    TimeLimit = 1,
    SizeLimit = 2
}

export enum AnnotationMessageType {
    AM_START_ANNOTATION = 0x1,
    AM_STOP_ANNOTATION = 0x2,
    AM_RESET_ANNOTATION = 0x3,
    AM_TOGGLE_ANNOTATION = 0x4,
    AM_CLEAR_ANNOTATION = 0x5,
    AM_SET_PEN = 0xA,
    AM_SET_ERASER = 0xB,
    AM_ANNOTATION_LASER = 0xC
}

export enum AnnotationOperatingModes {
    Annotation = 0x1,
    Whiteboard = 0x2
}

export enum AnnotationDrawingModes {
    /// <summary>
    /// Only adminsitrator can draw
    /// </summary>
    AdminOnly = 0x1,

    /// <summary>
    /// Both user and administrator can draw
    /// </summary>
    All = 0x2
}

export enum SotiAssistStatus {
    SAS_VIDEO_FOLDER_CREATION_FAILED = 0x01,
    SAS_VIDEO_ENCODING_SERVER_OCCUPIED = 0x02,
    SAS_VIDEO_MAX_DURATION_REACHED = 0x04,
    SAS_VIDEO_ATTACHMENT_CREATION_FAILED = 0x08,
    SAS_DOWNLOAD_LINK__CREATION_FAILED = 0x10,
    SAS_VIDEO_RECORDING_EMPTY = 0x20,
    SAS_VIDEO_FOLDER_PATH_TOO_LONG = 0x40,
    SAS_LOG_ATTACHMENT_CREATION_FAILED = 0x80,
    SAS_LOG_FILE_UNAVAILABLE_ON_AGENT = 0x100
}

export enum AndroidActivityManagement {
    AAM_GET_APP_LIST = 0,
    AAM_GET_APP_DETAILS = 1,
    AAM_GET_PROCESSES_LIST = 2,
    AAM_GET_PROCESS_DETAILS = 3,
    AAM_KILL_APPLICATION = 4,
    AAM_KILL_PROCESS = 5
}

export enum MouseActionType {
    MOUSEDOWN = 0,
    MOUSEUP = 1,
    RMOUSEDOWN = 2,
    RMOUSEUP = 3,
    MOUSEMOVE = 4
}

export interface IArea {
    left: number,
    top: number,
    right: number,
    bottom: number
}

export enum FileSystemErrorCodes {
    FileUploadStopRequested = 190,
    FileDownloadStopRequested = 191,
    RCPauseInterruptedFileAction = 192,
    FatalError = 193,
    FileMemoryAllocation = 194
}

/** TaskManager classes and interfaces */
export class TaskMemoryUsage {
    dalvikVM: number;
    native: number;
    other: number;
}

export class TaskError {
    condition: number;
    cause: string;
    conditionMessage: string;
}

export class TaskPackage {
    name: string
}

export class TaskThread {
    tid: number;
    pri: number;
}

export interface ITaskDetailsResponse {
    tasks: TaskPackage[];
    mem: TaskMemoryUsage;
    err: TaskError
}

export class TaskModule {
    baseAdd: number;
    baseSize: number;
    usage: number;
    procUsage: number;
    handle: number;
    name: string;
}

export namespace SystemInfo {
    export enum OsType {
        Unknown = 0,

        // WinCe
        WinCeMin = 10000,
        WinCe = 10000,
        WinCeNet = 10001,
        WinCePocketPc = 10002,
        WinCeSmartphone = 10003,
        WinCeWindowsMobile = 10004,
        WinCeMax = 20000,

        // Win Desktop
        WindowsMin = 30000,
        Windows = 30000,
        Windows95 = 30001,
        Windows98 = 30002,
        WindowsMe = 30003,
        WindowsNt351 = 30004,
        WindowsNt4 = 30005,
        Windows2000 = 30006,
        WindowsXp = 30007,
        WindowsServer2003 = 30008,
        WindowsVista = 30009,
        Windows7 = 30010,
        WindowsServer2008 = 30011,
        Windows8 = 30012,
        WindowsServer2012 = 30013,
        Windows81 = 30014,
        Windows10 = 30015,
        WindowsServer2016 = 30016,
        WindowsMax = 39999,

        // Universal Windows
        UniversalWindows = 40000,

        // iOS
        IosMin = 70000,
        Ios = 70000,
        IosMax = 80000,

        // Android
        AndroidMin = 90000,
        Android = 90000,
        AndroidMax = 99999,

        LinuxMin = 100000,
        LinuxMax = 109999,

        TizenMin = 110000,
        TizenMax = 119999,

        OsMax = 120000
    }

    export enum BatteryStatusAndroid {
        Unknown = 1,
        Charging = 2,
        Discharging = 3,
        NotCharging = 4,
        Full = 5
    }

    export enum BatteryStatusWM {
        High = 1,
        Low = 2,
        Critical = 4,
        Charging = 8,
        NoBattery = 128,
        Unknown = 255
    }
}
